import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, PlayCircle, Circle, BookOpen, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useReducedMotion from '../../hooks/useReducedMotion';
import { staggerContainer, fadeUp, maybe } from '../../lib/motionVariants';
import EmptyState from '../ui/EmptyState';

/**
 * CourseLessonList — context-aware 8-lesson window around the current lesson.
 *
 * Props:
 *   enrollment — enrollment object or null
 *   lessons    — array of lesson objects (may be empty)
 *
 * States (no locked — enrolled users can access all lessons):
 *   completed : CheckCircle2 (green, opacity-60)
 *   current   : PlayCircle   (brand-primary, highlighted bg + "Şimdi" badge)
 *   upcoming  : Circle       (gray-300, clickable)
 */
export default function CourseLessonList({ enrollment, lessons = [] }) {
  const { t, getField } = useLanguage();
  const reduced = useReducedMotion();

  if (!enrollment || !enrollment.course) {
    return (
      <EmptyState
        icon={BookOpen}
        iconSize={40}
        title={t('dashboard.duo.empty.title')}
        sub={t('dashboard.duo.empty.sub')}
        ctaLabel={t('dashboard.duo.empty.cta')}
        ctaTo="/courses"
      />
    );
  }

  const course = enrollment.course;
  const courseId = course.course_id || course._id || course.id;

  // Resolved lesson list
  const resolvedLessons = lessons.length > 0
    ? lessons
    : (enrollment.lessons || enrollment.course?.lessons || []);

  const totalLessons = resolvedLessons.length;

  // completed_lesson_ids from backend (prefer this, fallback to progress estimate)
  const completedIds = new Set(
    enrollment.completed_lesson_ids || enrollment.completed_lessons_ids || []
  );

  const progressPercent = enrollment.progress || 0;
  const estimatedCompletedCount =
    completedIds.size > 0
      ? completedIds.size
      : Math.floor(totalLessons * progressPercent / 100);

  // Current lesson index = first not-yet-completed lesson
  const currentIndex = estimatedCompletedCount;

  // --- Context-aware 8-lesson window ---
  let startIdx, endIdx;
  if (totalLessons <= 8) {
    startIdx = 0;
    endIdx = totalLessons;
  } else if (currentIndex <= 2) {
    startIdx = 0;
    endIdx = 8;
  } else if (currentIndex >= totalLessons - 3) {
    startIdx = Math.max(0, totalLessons - 8);
    endIdx = totalLessons;
  } else {
    startIdx = currentIndex - 2;
    endIdx = Math.min(currentIndex + 6, totalLessons);
  }

  const visibleLessons = resolvedLessons.slice(startIdx, endIdx);

  if (visibleLessons.length === 0) {
    return (
      <section className="card-duo p-5" data-testid="lesson-list-empty">
        <h2 className="font-duo-display text-base text-[var(--text-primary)] mb-1">
          {t('dashboard.continuePrograms')}
        </h2>
        <p className="text-sm text-gray-400 mb-4">{t('dashboard.duo.empty.sub')}</p>
        <Link
          to={`/courses/${courseId}`}
          className="text-[#16794a] font-bold text-sm flex items-center gap-1 hover:underline"
        >
          {t('dashboard.duo.allLessons')}
          <ChevronRight size={14} strokeWidth={2.5} />
        </Link>
      </section>
    );
  }

  return (
    <section
      className="card-duo p-5"
      aria-labelledby="lesson-list-heading"
      data-testid="lesson-list"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2
            id="lesson-list-heading"
            className="font-duo-display text-base text-[var(--text-primary)]"
          >
            {t('dashboard.continuePrograms')}
          </h2>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">
            {getField(course, 'title')}
          </p>
        </div>
      </div>

      <motion.ol
        className="space-y-2"
        variants={maybe(reduced, staggerContainer(0.05, 0.05))}
        initial="hidden"
        animate="visible"
        role="list"
        aria-label={t('dashboard.continuePrograms')}
      >
        {visibleLessons.map((lesson, i) => {
          const absoluteIndex = startIdx + i;
          const lid = lesson.lesson_id || lesson._id || lesson.id;

          const isCompleted =
            completedIds.has(lid) || absoluteIndex < estimatedCompletedCount;
          const isCurrent = absoluteIndex === currentIndex;
          // No locked state — enrolled users can always access lessons

          const lessonTitle = getField(lesson, 'title') ||
            `${t('dashboard.duo.lessonLabel')} ${absoluteIndex + 1}`;

          const targetUrl =
            courseId && lid
              ? `/lesson/${courseId}/${lid}`
              : null;

          const rowClass = isCurrent
            ? 'bg-[#f0faf4] border border-[#bfe6cf] rounded-xl'
            : isCompleted
            ? 'opacity-70 hover:opacity-100 transition-opacity rounded-xl hover:bg-[#f3faf5]'
            : 'rounded-xl hover:bg-[#f3faf5] transition-colors';

          const row = (
            <motion.li
              key={lid || absoluteIndex}
              variants={maybe(reduced, fadeUp)}
              className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${rowClass}`}
              role="listitem"
            >
              {/* State icon */}
              {isCompleted ? (
                <CheckCircle2
                  size={20}
                  strokeWidth={2}
                  className="text-[#34CC67] flex-shrink-0"
                  aria-hidden="true"
                />
              ) : isCurrent ? (
                <PlayCircle
                  size={20}
                  strokeWidth={2}
                  className="text-[#16794a] flex-shrink-0"
                  aria-hidden="true"
                />
              ) : (
                <Circle
                  size={20}
                  strokeWidth={2}
                  className="text-[#cdd6cf] flex-shrink-0"
                  aria-hidden="true"
                />
              )}

              {/* Order + title */}
              <span className={`flex-1 text-sm truncate ${isCurrent ? 'font-bold' : 'font-semibold'} ${isCompleted || isCurrent ? 'text-[#0E1F3D]' : 'text-[#6b7a70]'}`}>
                <span className="text-[10px] font-normal text-[#9aa89f] mr-1.5">
                  {lesson.order || absoluteIndex + 1}.
                </span>
                {lessonTitle}
              </span>

              {/* "Şimdi" badge */}
              {isCurrent && (
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#16794a] text-white flex-shrink-0">
                  {t('dashboard.duo.lessonNow')}
                </span>
              )}
            </motion.li>
          );

          return targetUrl ? (
            <Link
              key={lid || absoluteIndex}
              to={targetUrl}
              className="block no-underline"
              aria-label={
                isCurrent
                  ? `${t('dashboard.duo.lessonNow')}: ${lessonTitle}`
                  : isCompleted
                  ? `${t('dashboard.duo.lessonCompleted') || 'Tamamlandı'}: ${lessonTitle}`
                  : lessonTitle
              }
            >
              {row}
            </Link>
          ) : (
            <div key={lid || absoluteIndex} className="block">
              {row}
            </div>
          );
        })}
      </motion.ol>

      {/* All lessons link */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <Link
          to={`/courses/${courseId}`}
          className="text-[#16794a] font-bold text-sm flex items-center gap-1 hover:underline"
          data-testid="all-lessons-link"
        >
          {t('dashboard.duo.allLessons')}
          <ChevronRight size={14} strokeWidth={2.5} />
        </Link>
      </div>
    </section>
  );
}
